import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  PiggyBank, 
  LineChart, 
  TrendingUp, 
  BarChart4, 
  Briefcase,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PortfolioView } from "@/components/invest/PortfolioView";
import { MarketView } from "@/components/invest/MarketView";
import { MarketSimulator } from "@/components/games/MarketSimulator";
import BetaModal from "@/components/invest/BetaModal";

/**
 * Invest component that displays investment options and market data
 * This page provides various investment tools and resources for the user
 * Currently in beta version with simulated data and features
 */
const Invest = () => {
  const [selectedTab, setSelectedTab] = useState("dashboard");
  const [showNotifications, setShowNotifications] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(true); // Show beta modal by default
  const [notifications, setNotifications] = useState([
    { id: 1, text: "AAPL price alert: Up 2% today", read: false },
    { id: 2, text: "Your weekly portfolio summary is ready", read: false },
    { id: 3, text: "New investment opportunity detected", read: true },
  ]);

  // Check if user has seen the beta modal before
  useEffect(() => {
    const hasSeenBetaModal = localStorage.getItem('invest-beta-modal-seen');
    if (hasSeenBetaModal) {
      setShowBetaModal(false);
    }
  }, []);

  // Handle closing the beta modal
  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    localStorage.setItem('invest-beta-modal-seen', 'true');
  };

  const investmentOptions = [
    {
      title: "Start Investing",
      description: "Begin your investment journey with guided portfolios",
      icon: DollarSign,
    },
    {
      title: "Save & Grow",
      description: "Set up automatic savings and watch your money grow",
      icon: PiggyBank,
    },
    {
      title: "Track Performance",
      description: "Monitor your investments and analyze returns",
      icon: LineChart,
    },
  ];

  const additionalOptions = [
    {
      title: "Research Markets",
      description: "Access market data and research reports",
      icon: BarChart4,
    },
    {
      title: "Portfolio Analysis",
      description: "Get insights and recommendations for your portfolio",
      icon: BarChart4,
    },
    {
      title: "Investment Education",
      description: "Learn investment strategies and concepts",
      icon: Briefcase,
    },
  ];

  const allOptions = [...investmentOptions, ...additionalOptions];

  return (
    <>
      {/* Beta Modal */}
      <BetaModal isOpen={showBetaModal} onClose={handleCloseBetaModal} />
      
      <div className="space-y-8 animate-fade-in">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Invest
              </h1>
              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                BETA
              </Badge>
            </div>
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative"
              >
                <Bell className="h-5 w-5" />
                {notifications.some(n => !n.read) && (
                  <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500" />
                )}
              </Button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg z-50 border">
                  <div className="p-3 border-b">
                    <h3 className="font-semibold">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map(notification => (
                      <div 
                        key={notification.id} 
                        className={`p-3 border-b hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : ''}`}
                      >
                        <p className="text-sm">{notification.text}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.read ? 'Read' : 'New'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="p-2 text-center">
                    <Button variant="link" size="sm" className="text-xs">
                      Mark all as read
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-gray-600">Start your investment journey</p>
            <Badge variant="outline" className="text-xs text-gray-500">
              Demo version with simulated data
            </Badge>
          </div>
        </div>

        <Tabs defaultValue="dashboard" onValueChange={setSelectedTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="market">Market</TabsTrigger>
            <TabsTrigger value="simulator">Simulator</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard" className="space-y-6">
            {/* Portfolio View Component */}
            <PortfolioView />

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Market News</CardTitle>
                <CardDescription>Latest updates that might affect your investments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    {
                      id: 1,
                      title: "Fed Raises Interest Rates by 0.25%",
                      source: "Financial Times",
                      time: "2 hours ago",
                      impact: "high",
                    },
                    {
                      id: 2,
                      title: "Tech Stocks Rally Following Positive Earnings Reports",
                      source: "Wall Street Journal",
                      time: "4 hours ago",
                      impact: "medium",
                    },
                    {
                      id: 3,
                      title: "New Regulations for Cryptocurrency Exchanges Announced",
                      source: "Bloomberg",
                      time: "6 hours ago",
                      impact: "medium",
                    },
                    {
                      id: 4,
                      title: "Housing Market Shows Signs of Cooling Down",
                      source: "CNBC",
                      time: "8 hours ago",
                      impact: "low",
                    },
                  ].map(news => (
                    <div key={news.id} className="border-b pb-4 last:border-0 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{news.title}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <span>{news.source}</span>
                            <span>•</span>
                            <span>{news.time}</span>
                          </div>
                        </div>
                        <div className={`px-2 py-1 text-xs rounded-full ${
                          news.impact === "high" ? 'bg-red-100 text-red-800' :
                          news.impact === "medium" ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {news.impact.charAt(0).toUpperCase() + news.impact.slice(1)} Impact
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allOptions.map((option, index) => (
                <Card
                  key={index}
                  className="group border border-gray-100/50 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden hover:bg-white/80 backdrop-blur-sm"
                >
                  <div className="p-6 relative">
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-transparent" />
                    </div>
                    
                    <div className="relative flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-secondary/10 group-hover:scale-105 transition-transform duration-300">
                        <option.icon className="w-6 h-6 text-secondary" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                          {option.title}
                        </h3>
                        <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors">
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="market" className="space-y-6">
            {/* Market View Component */}
            <MarketView />
          </TabsContent>

          <TabsContent value="simulator" className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Market Simulator</CardTitle>
                <CardDescription>Practice investing without real money</CardDescription>
              </CardHeader>
              <CardContent>
                <MarketSimulator />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default Invest;
